import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, ViewChild, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { VideoCallStateService } from '../../services/video-call-state.service';

/**
 * Montado UNA sola vez en `AppComponent` (mismo patrón que `ClipRecordingIndicatorComponent`):
 * lee el estado de `VideoCallStateService` y pinta según la fase -- banner de llamada entrante,
 * "Llamando…", o la UI completa con los dos videos. No inicia ni recibe llamadas por su cuenta,
 * todo ese trabajo vive en el servicio; este componente es solo la vista.
 */
@Component({
  selector: 'app-video-call-overlay',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (state.phase() === 'incoming') {
      <div class="fixed top-4 right-4 z-[100001] w-80 rounded-xl border border-gray-200 dark:border-gray-700
                  bg-white dark:bg-gray-900 shadow-lg p-4 flex items-center gap-3" role="alert">
        <div class="w-11 h-11 rounded-full bg-brand-50 dark:bg-brand-500/15 flex items-center justify-center flex-shrink-0">
          <i class="bi bi-camera-video-fill text-brand-500 text-lg animate-pulse"></i>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-bold text-gray-900 dark:text-white">{{ 'nexus.messaging.videollamadaEntrante' | transloco }}</p>
          <div class="flex gap-2 mt-2">
            <button type="button" class="flex-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-brand-500 text-white hover:bg-brand-600"
                    (click)="state.acceptIncomingCall()">
              <i class="bi bi-telephone-fill mr-1"></i>{{ 'nexus.messaging.contestar' | transloco }}
            </button>
            <button type="button" class="flex-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600
                           text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    (click)="state.declineIncomingCall()">
              {{ 'nexus.messaging.rechazar' | transloco }}
            </button>
          </div>
        </div>
      </div>
    }

    @if (state.phase() === 'outgoing' || state.phase() === 'connecting' || state.phase() === 'active') {
      <div class="fixed inset-0 z-[100000] bg-gray-950/95 flex flex-col items-center justify-center gap-4 p-4">
        <div class="relative w-full max-w-3xl aspect-video rounded-2xl overflow-hidden bg-gray-900 border border-gray-800">
          <video #remoteVideo autoplay playsinline class="w-full h-full object-cover"
                 [class.hidden]="!state.remoteStream()"></video>
          @if (!state.remoteStream()) {
            <div class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-400">
              <i class="bi bi-camera-video text-3xl"></i>
              <p class="text-sm">
                {{ (state.phase() === 'outgoing' ? 'nexus.messaging.llamando' : 'nexus.messaging.conectando') | transloco }}
              </p>
            </div>
          }
          <video #localVideo autoplay playsinline muted
                 class="absolute bottom-3 right-3 w-32 aspect-video rounded-lg object-cover border-2 border-gray-800 shadow-lg"
                 [class.hidden]="!state.localStream() || state.cameraOff()"></video>
        </div>

        @if (state.mediaError(); as err) {
          <p class="text-xs text-red-400 max-w-md text-center">{{ err }}</p>
        }

        <div class="flex items-center gap-3">
          <button type="button" class="w-12 h-12 rounded-full flex items-center justify-center text-white
                         hover:opacity-90"
                  [class]="state.micMuted() ? 'bg-gray-600' : 'bg-gray-700'"
                  (click)="state.toggleMic()"
                  [attr.aria-label]="(state.micMuted() ? 'nexus.messaging.activarMicrofono' : 'nexus.messaging.silenciarMicrofono') | transloco">
            <i class="bi" [class.bi-mic-mute-fill]="state.micMuted()" [class.bi-mic-fill]="!state.micMuted()"></i>
          </button>
          <button type="button" class="w-12 h-12 rounded-full flex items-center justify-center text-white hover:opacity-90"
                  [class]="state.cameraOff() ? 'bg-gray-600' : 'bg-gray-700'"
                  (click)="state.toggleCamera()"
                  [attr.aria-label]="(state.cameraOff() ? 'nexus.messaging.activarCamara' : 'nexus.messaging.apagarCamara') | transloco">
            <i class="bi" [class.bi-camera-video-off-fill]="state.cameraOff()" [class.bi-camera-video-fill]="!state.cameraOff()"></i>
          </button>
          <button type="button" class="w-14 h-14 rounded-full flex items-center justify-center text-white bg-red-500 hover:bg-red-600"
                  (click)="state.hangUp()" [attr.aria-label]="'nexus.messaging.colgar' | transloco">
            <i class="bi bi-telephone-x-fill text-lg"></i>
          </button>
        </div>
      </div>
    }
  `,
})
export class VideoCallOverlayComponent implements AfterViewInit {
  readonly state = inject(VideoCallStateService);

  @ViewChild('localVideo') localVideoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteVideo') remoteVideoRef?: ElementRef<HTMLVideoElement>;

  constructor() {
    // Los `<video>` solo existen en el DOM mientras la llamada está activa (el `@if` de arriba
    // los crea/destruye) -- hay que reconectar `srcObject` cada vez que reaparecen, un binding
    // `[srcObject]` no alcanza a tiempo si el stream ya estaba listo antes de que el elemento
    // existiera. Se lee `phase()` además del stream para que el efecto también se dispare justo
    // cuando el `<video>` aparece en el DOM (no solo cuando cambia el stream en sí), y
    // `queueMicrotask` para leer la referencia después de que Angular ya actualizó el DOM de este
    // ciclo -- mismo patrón que el resto del proyecto usa para esto (scrollToMessage, etc.).
    effect(() => {
      const local = this.state.localStream();
      this.state.phase();
      queueMicrotask(() => {
        const el = this.localVideoRef?.nativeElement;
        if (el) el.srcObject = local;
      });
    });
    effect(() => {
      const remote = this.state.remoteStream();
      this.state.phase();
      queueMicrotask(() => {
        const el = this.remoteVideoRef?.nativeElement;
        if (el) el.srcObject = remote;
      });
    });
  }

  ngAfterViewInit(): void {
    if (this.localVideoRef) this.localVideoRef.nativeElement.srcObject = this.state.localStream();
    if (this.remoteVideoRef) this.remoteVideoRef.nativeElement.srcObject = this.state.remoteStream();
  }
}
